/**
 * Scenario definitions shared by the join screen and the four room views.
 *
 * A scenario is a *capability envelope* (who may publish, how many people, seats) plus the copy
 * shown for it. `?scene=meeting|1v1|live|voice` selects it; the route is shareable and survives a
 * reload, and every room re-exports it in its own invite link.
 */
import type { MessageKey } from '../i18n/messages';

export type SceneId = 'meeting' | '1v1' | 'live' | 'voice';
export type MeetingRole = 'host' | 'audience';

export interface SceneSpec {
  id: SceneId;
  /** Hard participant cap for two-party style scenes; `null` means unbounded. */
  maxParticipants: number | null;
  audioOnly: boolean;
  /** Mic seats for a voice room; `null` for scenes without seats. */
  seats: number | null;
  /** Whether the audience role is allowed to publish media. Always false here: the token denies it. */
  audienceCanPublish: boolean;
  allowedRoles: readonly MeetingRole[];
  /** Video publish is forced off for the audience regardless of the join form toggles. */
  forceAudioOnlyForAudience: boolean;
}

export const SCENES: Record<SceneId, SceneSpec> = {
  meeting: {
    id: 'meeting',
    maxParticipants: null,
    audioOnly: false,
    seats: null,
    audienceCanPublish: false,
    allowedRoles: ['host', 'audience'],
    forceAudioOnlyForAudience: false,
  },
  '1v1': {
    id: '1v1',
    maxParticipants: 2,
    audioOnly: false,
    seats: null,
    audienceCanPublish: false,
    // A 1v1 call is two publishing parties: the audience role is rejected up front.
    allowedRoles: ['host'],
    forceAudioOnlyForAudience: false,
  },
  live: {
    id: 'live',
    maxParticipants: null,
    audioOnly: false,
    seats: null,
    audienceCanPublish: false,
    allowedRoles: ['host', 'audience'],
    forceAudioOnlyForAudience: true,
  },
  voice: {
    id: 'voice',
    maxParticipants: null,
    audioOnly: true,
    seats: 8,
    audienceCanPublish: false,
    allowedRoles: ['host', 'audience'],
    forceAudioOnlyForAudience: true,
  },
};

export const SCENE_ORDER: readonly SceneId[] = ['meeting', '1v1', 'live', 'voice'];

export function isSceneId(value: unknown): value is SceneId {
  return value === 'meeting' || value === '1v1' || value === 'live' || value === 'voice';
}

/** Parse `?scene=` (accepts `voice-room` / `voiceRoom` aliases from older links). */
export function parseScene(raw: string | null | undefined): SceneId {
  const value = (raw ?? '').trim().toLowerCase();
  if (value === '1v1' || value === '1-1' || value === 'call' || value === 'one-to-one')
    return '1v1';
  if (value === 'live' || value === 'interactive-live') return 'live';
  if (
    value === 'voice' ||
    value === 'voice-room' ||
    value === 'voiceroom' ||
    value === 'voice_room'
  )
    return 'voice';
  return 'meeting';
}

export function sceneFromSearch(search: string): SceneId {
  try {
    return parseScene(
      new URLSearchParams(search.startsWith('?') ? search : `?${search}`).get('scene'),
    );
  } catch {
    return 'meeting';
  }
}

export function isRoleAllowed(scene: SceneId, role: MeetingRole): boolean {
  return SCENES[scene].allowedRoles.includes(role);
}

export interface SceneJoinValidation {
  ok: boolean;
  errorKey: MessageKey | null;
}

/** Rejects joins the scenario cannot honour, before any token is requested. */
export function validateSceneJoin(scene: SceneId, role: MeetingRole): SceneJoinValidation {
  if (!isRoleAllowed(scene, role)) return { ok: false, errorKey: 'join.error.roleDenied' };
  return { ok: true, errorKey: null };
}

/** What the join form publishes for a (scene, role) pair; the token still has the final say. */
export function defaultPublishFor(
  scene: SceneId,
  role: MeetingRole,
): { audio: boolean; video: boolean } {
  const spec = SCENES[scene];
  if (role === 'audience') return { audio: false, video: false };
  return { audio: true, video: !spec.audioOnly };
}

export function sceneNameKey(scene: SceneId): MessageKey {
  switch (scene) {
    case '1v1':
      return 'scene.oneToOne';
    case 'live':
      return 'scene.live';
    case 'voice':
      return 'scene.voice';
    default:
      return 'scene.meeting';
  }
}

export function sceneTitleKey(scene: SceneId): MessageKey {
  switch (scene) {
    case '1v1':
      return 'scene.oneToOne.title';
    case 'live':
      return 'scene.live.title';
    case 'voice':
      return 'scene.voice.title';
    default:
      return 'scene.meeting.title';
  }
}

export function sceneDescriptionKey(scene: SceneId): MessageKey {
  switch (scene) {
    case '1v1':
      return 'scene.oneToOne.desc';
    case 'live':
      return 'scene.live.desc';
    case 'voice':
      return 'scene.voice.desc';
    default:
      return 'scene.meeting.desc';
  }
}

/** Replace `?scene=` while keeping other invite parameters (channel, from, lang, theme). */
export function withScene(search: string, scene: SceneId): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (scene === 'meeting') params.delete('scene');
  else params.set('scene', scene);
  const query = params.toString();
  return query.length > 0 ? `?${query}` : '';
}
